'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useAppSelector } from '@/lib/hooks';
import { Button } from '@/components/ui/button';
import {
  Mail,
  Inbox,
  BarChart3,
  ArrowRight,
  Upload,
  Sparkles,
  Bot,
  CheckCircle2,
  Zap
} from 'lucide-react';

export default function LandingPage() {
  const router = useRouter();
  const { isAuthenticated, isHydrated } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (isHydrated && isAuthenticated) {
      router.push('/pipeline');
    }
  }, [isAuthenticated, isHydrated, router]);

  // Show loading while checking auth
  if (!isHydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // If authenticated, show loading while redirecting
  if (isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <Zap className="h-5 w-5 text-white" />
              </div>
              <span className="text-xl font-bold">Nozo</span>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" asChild>
                <Link href="/login">Sign in</Link>
              </Button>
              <Button asChild>
                <Link href="/signup">
                  Get Early Access
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 text-sm font-medium mb-8">
            <Sparkles className="h-4 w-4" />
            AI-Powered Sales Automation
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
            The CRM that{' '}
            <span className="bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
              closes deals
            </span>{' '}
            for you.
          </h1>
          <p className="text-xl sm:text-2xl text-muted-foreground mb-10 max-w-2xl mx-auto">
            AI-powered sales for founders. Less admin. More closed deals.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" className="text-lg px-8 py-6" asChild>
              <Link href="/signup">
                Get Early Access
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground">
              Free during beta. No credit card required.
            </p>
          </div>
        </div>

        {/* Hero Visual */}
        <div className="max-w-5xl mx-auto mt-16">
          <div className="relative rounded-xl overflow-hidden border shadow-2xl bg-gradient-to-b from-muted/50 to-muted">
            <div className="absolute inset-0 bg-gradient-to-tr from-violet-500/10 via-transparent to-purple-500/10" />
            <div className="aspect-[16/9] flex items-center justify-center p-8">
              <div className="grid grid-cols-3 gap-4 w-full max-w-3xl">
                {/* Pipeline Preview Card */}
                <div className="col-span-2 bg-background rounded-lg shadow-lg border p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="h-3 w-3 rounded-full bg-red-400" />
                    <div className="h-3 w-3 rounded-full bg-yellow-400" />
                    <div className="h-3 w-3 rounded-full bg-green-400" />
                  </div>
                  <div className="flex gap-3">
                    {['Lead', 'Contacted', 'Qualified', 'Proposal'].map((stage, i) => (
                      <div key={stage} className="flex-1">
                        <div className="text-xs font-medium text-muted-foreground mb-2">{stage}</div>
                        <div className="space-y-2">
                          {[...Array(3 - i)].map((_, j) => (
                            <div key={j} className="h-12 rounded-md bg-muted animate-pulse" />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* AI Assistant Card */}
                <div className="bg-background rounded-lg shadow-lg border p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Bot className="h-5 w-5 text-violet-500" />
                    <span className="text-sm font-medium">AI Assistant</span>
                  </div>
                  <div className="space-y-2">
                    <div className="h-3 rounded bg-muted w-full" />
                    <div className="h-3 rounded bg-muted w-4/5" />
                    <div className="h-3 rounded bg-muted w-3/5" />
                  </div>
                  <div className="mt-4 p-2 rounded-md bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800">
                    <p className="text-xs text-violet-700 dark:text-violet-300">
                      "I've drafted a follow-up email for the Acme deal..."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            You're a founder, not a data entry clerk.
          </h2>
          <p className="text-xl text-muted-foreground mb-4">
            Every hour you spend updating your CRM is an hour you're not selling.
          </p>
          <p className="text-xl text-muted-foreground">
            And let's be honest—<span className="text-foreground font-medium">your pipeline is already out of date.</span>
          </p>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              A CRM that actually helps you sell.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* Feature 1 */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-violet-500 to-purple-500 rounded-2xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity" />
              <div className="relative bg-background border rounded-2xl p-6 h-full">
                <div className="h-12 w-12 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center mb-4">
                  <Mail className="h-6 w-6 text-violet-600 dark:text-violet-400" />
                </div>
                <h3 className="text-lg font-semibold mb-2">AI writes your emails</h3>
                <p className="text-muted-foreground">
                  Personalized outreach in seconds. No more staring at blank drafts.
                </p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-violet-500 to-purple-500 rounded-2xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity" />
              <div className="relative bg-background border rounded-2xl p-6 h-full">
                <div className="h-12 w-12 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center mb-4">
                  <Inbox className="h-6 w-6 text-violet-600 dark:text-violet-400" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Smart inbox</h3>
                <p className="text-muted-foreground">
                  Replies classified, sequences paused automatically. Never miss a hot lead.
                </p>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-violet-500 to-purple-500 rounded-2xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity" />
              <div className="relative bg-background border rounded-2xl p-6 h-full">
                <div className="h-12 w-12 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center mb-4">
                  <BarChart3 className="h-6 w-6 text-violet-600 dark:text-violet-400" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Pipeline stays current</h3>
                <p className="text-muted-foreground">
                  AI suggests stage changes based on what's actually happening.
                </p>
              </div>
            </div>
          </div>

          <p className="text-center text-lg text-muted-foreground">
            No enterprise bloat. No 47-field forms. <span className="text-foreground font-medium">Just close deals.</span>
          </p>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Get started in 15 minutes.
            </h2>
          </div>

          <div className="space-y-8">
            {/* Step 1 */}
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0 h-12 w-12 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                <Upload className="h-6 w-6 text-violet-600 dark:text-violet-400" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-sm font-medium text-violet-600 dark:text-violet-400">Step 1</span>
                  <h3 className="text-xl font-semibold">Add prospects</h3>
                </div>
                <p className="text-muted-foreground">
                  Import a CSV or add contacts manually. Your data, your way.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0 h-12 w-12 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-violet-600 dark:text-violet-400" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-sm font-medium text-violet-600 dark:text-violet-400">Step 2</span>
                  <h3 className="text-xl font-semibold">Generate outreach</h3>
                </div>
                <p className="text-muted-foreground">
                  AI writes personalized emails based on your prospect's context.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0 h-12 w-12 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                <Bot className="h-6 w-6 text-violet-600 dark:text-violet-400" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-sm font-medium text-violet-600 dark:text-violet-400">Step 3</span>
                  <h3 className="text-xl font-semibold">Nozo handles the rest</h3>
                </div>
                <p className="text-muted-foreground">
                  Follow-ups, replies, pipeline updates. All automated.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 p-6 rounded-2xl bg-gradient-to-r from-violet-500/10 to-purple-500/10 border border-violet-200 dark:border-violet-800 text-center">
            <p className="text-lg font-medium">
              You just show up to the meetings.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to stop babysitting your CRM?
          </h2>
          <div className="flex flex-col items-center gap-4">
            <Button size="lg" className="text-lg px-8 py-6" asChild>
              <Link href="/signup">
                Get Early Access
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground">
              Free during beta. No credit card required.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 rounded-md bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <Zap className="h-4 w-4 text-white" />
              </div>
              <span className="font-semibold">Nozo</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2025 Nozo. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link href="#" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link href="#" className="hover:text-foreground transition-colors">Terms</Link>
              <Link href="#" className="hover:text-foreground transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
